<template>
	<div
		class="scoreboard"
	>
		<div class="team-penalty-col">
			<div class="team">
				<div class="logo">
					<img
						class="participant-image"
						:src="homeTeamImage"
					>
				</div>
				<div class="team-code">
					{{ homeTeamLabel }}
				</div>
			</div>
			<div
				v-if="penalties[homeTeamId][0]"
				class="penalty"
			>
				{{ penalties[homeTeamId][0] }}
			</div>
			<div
				v-if="penalties[homeTeamId][1]"
				class="penalty"
			>
				{{ penalties[homeTeamId][1] }}
			</div>
			<div
				v-if="penalties[homeTeamId][2]"
				class="penalty"
			>
				{{ penalties[homeTeamId][2] }}
			</div>
		</div>
		<div class="score">
			<div class="score-upper">
				<div v-if="showTime">
					{{ currentTime }}
				</div>
			</div>
			<div class="score-content">
				<div class="left-score">
					{{ homeTeamScore }}
				</div>
				<div class="score-divider" />
				<div class="right-score">
					{{ guestTeamScore }}
				</div>
			</div>
		</div>
		<div class="team-penalty-col">
			<div class="team">
				<div class="logo">
					<img
						class="participant-image"
						:src="guestTeamImage"
					>
				</div>
				<div class="team-code">
					{{ guestTeamLabel }}
				</div>
			</div>
			<div
				v-if="penalties[guestTeamId][0]"
				class="penalty"
			>
				{{ penalties[guestTeamId][0] }}
			</div>
			<div
				v-if="penalties[guestTeamId][1]"
				class="penalty"
			>
				{{ penalties[guestTeamId][1] }}
			</div>
			<div
				v-if="penalties[guestTeamId][2]"
				class="penalty"
			>
				{{ penalties[guestTeamId][2] }}
			</div>
		</div>
	</div>
</template>

<script>
import { getPenalties, CLOCK_START, CLOCK_SET, CLOCK_STOP } from '@js/logic/game/futsal';
import { padStart } from 'lodash-es';

export default {
	name:  'Scoreboard',
	props: {
		players: {
			type: Array,
		},
		scoreZones: {
			type: Array,
		},
		tags: {
			type: Array,
		},
		event: {
			type: Object,
		},
		position: {
			type: Number,
		},
		periods: {
			type: Array,
		},
		currentScoreZone: {
			type: Object,
		},
		gameTime: {
			type: Object,
		},
		showTime: {
			type:    Boolean,
			default: true,
		},
	},
	computed: {
		homeTeam() {
			return this.event.participants.find((item) => item.type === 'home');
		},
		guestTeam() {
			return this.event.participants.find((item) => item.type === 'guest');
		},
		homeTeamScore() {
			return Object.values(this.currentScoreZone.score)
				.find((s) => s.participant.participatable.id === this.homeTeamId).value;
		},
		guestTeamScore() {
			return Object.values(this.currentScoreZone.score)
				.find((s) => s.participant.participatable.id === this.guestTeamId).value;
		},
		homeTeamLabel() {
			return this.homeTeam?.participatable.club.code;
		},
		guestTeamLabel() {
			return this.guestTeam?.participatable.club.code;
		},
		homeTeamImage() {
			return this.homeTeam?.participatable.imageUrl;
		},
		guestTeamImage() {
			return this.guestTeam?.participatable.imageUrl;
		},
		guestTeamId() {
			return this.guestTeam.participatable.id;
		},
		homeTeamId() {
			return this.homeTeam.participatable.id;
		},
		currentTime() {
			const { seconds, minutes } = this.gameTime;

			return `${padStart(minutes, 2, '0')}:${padStart(Math.floor(seconds), 2, '0')}`;
		},
		sortedTags() {
			const sortedTags = Array.from(this.tags);
			sortedTags.sort((a, b) => a.timestamp - b.timestamp);
			return sortedTags;
		},
		timingTags() {
			return this.sortedTags.filter((eventTag) => {
				return [CLOCK_START, CLOCK_SET, CLOCK_STOP]
					.includes(eventTag.tag.name) && eventTag.timestamp < this.position;
			});
		},
		penalties() {
			return getPenalties({
				position:    this.position,
				event:       this.event,
				tags:        this.tags,
				periods:     this.periods,
				homeTeamId:  this.homeTeamId,
				guestTeamId: this.guestTeamId,
			});
		},
	},
};
</script>

<style lang="scss">
@import '@videotools/sass/fonts';

.scoreboard {
	display: flex;
	text-align: center;
	@include text-lg-2(true);

	.team-penalty-col {
		display: flex;
		flex-direction: column;

		.team {
			display: flex;
			flex-direction: column;
			justify-content: center;
			align-items: center;
			background: var(--color-grey-850);
			width: 64px;
			height: 100px;
			@include text-md-2;
			font-weight: 600;

			body.-t-light & {
				background-color: var(--color-grey-200);
			}

			.participant-image {
				width: 48px;
				height: 48px;
			}
		}

		.penalty {
			display: flex;
			justify-content: center;
			align-items: center;
			background: var(--color-red);
			width: 64px;
			height: 24px;
			@include text-md-1;
			font-weight: 600;

			&:not(:last-child) {
				border-bottom: solid 1px var(--color-soft-white);
			}
		}
	}

	.score {
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		background: var(--color-shadow-grey);
		width: 161px;
		height: 100px;

		.score-upper {
			display: flex;
			@include text-md-2;
			font-weight: 600;
			margin-bottom: 4px;

			.halftime {
				margin-right: 31px;
			}
		}

		.score-content {
			display: flex;
			align-items: center;
			font-size: 48px;
			font-weight: 600;
			line-height: 56px;

			.score-divider {
				height: 35px;
				width: 1px;
				background: var(--color-grey-300);
				margin-left: 25px;
				margin-right: 25px;
			}
		}
	}
}
</style>
